import { Component, Input, OnInit } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { AssistanceImageRequest, CompanyResponse, CompanyService } from '@src/app/shared/backend/clients';
import { ToasterService } from '@src/app/shared/components/toaster/toaster.service';
import { SessionService } from '@src/app/shared/services/session.service';
import { firstValueFrom } from 'rxjs';

@Component({
    selector: 'app-features-management',
    templateUrl: './features-management.component.html',
    styleUrls: ['./features-management.component.scss'],
    host: {
        class: 'app-features-management'
    },
    standalone: false
})
export class FeaturesManagementComponent implements OnInit {

  @Input() isUpdate = true;
  @Input() disableIsPrepaired = true;

  company: CompanyResponse = {};
  enabledFeatures: any[] = [];
  featuresList: any[] = [];
  featuresListDisplay: any[] = [];
  inventorySubmissionThresholdDays: any = 30;
  warrantyUseLimit: any;
  digitalPassportUrl: any;
  isDigitalPassportSecurityPasscodeRequired: boolean = false;
  hasLimitedEdition: any;
  hasCpo: any;
  hasPrice: any;
  showProductAttributeDetails: any;
  showRetailerAttributeDetails: any;
  directImportEnabled: any;
  allowSerialNumberCreationDuringSale: any;
  isLegacy: any;
  implicitSaleCreation: any;
  businessGroup: any;
  hasItemsWithoutSerialNumber: any;
  setSerialNumberAssistanceImage: any;
  assistanceSerialNumberImage: any;
  imageLoadFailed: any;
  imageUpdated: any;
  assistanceSerialNumberImagePath: any;
  fileUploadError: any;
  file: any;
  assistanceImagePath: any;
  allowItemOperationsSoldByAnotherRetailer: any;

  allowPasswordChange: any;
  isMfaMandatory: any;

  isPrepaired: any;

  showSecurityCode: any;
  showSecurityCodeVersion: any;
  repairOnlySoldItems: any;

  stateOptions = [
    {
      label: this.translate.instant('BOOLEANS.ACTIVATE_SWITCH'), value: true
    },
    { label: this.translate.instant('BOOLEANS.DEACTIVATE_SWITCH'), value: false }
  ];


  constructor(private session: SessionService,
    public translate: TranslateService,
    public toasterService: ToasterService,
    private companyService: CompanyService,
    private sanitizer: DomSanitizer) { }

  async ngOnInit() {
    this.company = await this.session.getCompanyData();

    this.featuresList = this.session.getFeaturesList();
    this.featuresListDisplay = this.featuresList.filter(f => f.name != "Basic");
    this.featuresListDisplay.push({name : "Security"});
    this.enabledFeatures = this.session.getFeatures();

    this.inventorySubmissionThresholdDays = this.company.inventorySubmissionThresholdDays;
    this.warrantyUseLimit = this.company.warrantyUseLimit;
    this.digitalPassportUrl = this.company.digitalPassportUrl;
    this.isDigitalPassportSecurityPasscodeRequired = this.company.isDigitalPassportSecurityPasscodeRequired ?? false;
    this.hasLimitedEdition = this.company.hasLimitedEdition ? '1': '0';
    this.hasCpo = this.company.hasCpo ? '1': '0';
    this.hasPrice = this.company.hasPrice ? '1': '0';
    this.directImportEnabled = this.company.directImportEnabled ? '1': '0';
    this.showProductAttributeDetails = this.company.showProductAttributeDetails;
    this.showRetailerAttributeDetails = this.company.showRetailerAttributeDetails;

    this.allowSerialNumberCreationDuringSale = this.company.canCreateItemOnSale;
    this.implicitSaleCreation = this.company.implicitSaleCreation ? '1': '0';
    this.isLegacy = this.company.showIsLegacy;

    this.isPrepaired = this.company.isPrepaired ? 'true' : 'false';

    this.hasItemsWithoutSerialNumber = this.company?.hasItemsWithoutSerialNumber ? '1' : '0';

    this.isMfaMandatory = this.company.isMfaMandatory;
    this.allowPasswordChange = this.company.allowUserChangePassword;

    this.repairOnlySoldItems = this.company.repairOnlySoldItems;
    this.showSecurityCodeVersion = this.company.showSecurityCodeVersion;
    this.showSecurityCode = this.company.showSecurityCode;

    this.businessGroup = this.company.hasRetailerBusinessGroup;

    this.allowItemOperationsSoldByAnotherRetailer = this.company.allowItemOperationsSoldByAnotherRetailer;

    this.assistanceSerialNumberImagePath = this.company?.assistanceImagePath ?? null;
    const hasImg = !!this.assistanceSerialNumberImagePath;
    this.setSerialNumberAssistanceImage = hasImg ? '1': '0';
    this.assistanceImagePath = null;
    if (hasImg) {
      try {
        const resp = await firstValueFrom(this.companyService.apiCompanyAssistanceImageGet());
        const base64 = resp?.response ?? null;
        this.assistanceSerialNumberImage = base64 ? this.sanitizer.bypassSecurityTrustUrl(base64) : null;
      } catch (error) {
        console.error('Error fetching assistance image:', error);
      }
    } else {
      this.assistanceSerialNumberImage = null;
    }

    console.log(this.company, this.assistanceSerialNumberImagePath);
  }

  getFeatureStateLabel(optionValue: boolean, feature: any): string {
    const featureIsActive = this.getFeatureValue(feature);
    const optionIsSelected = optionValue === featureIsActive;

    if (optionValue) {
      const translationKey = optionIsSelected ? 'SYSTEM_SETTINGS.MODULES.FEATURE_STATUS_ACTIVATED' : 'BOOLEANS.ACTIVATE_SWITCH';

      return this.translate.instant(translationKey);
    }

    const translationKey = optionIsSelected ? 'SYSTEM_SETTINGS.MODULES.FEATURE_STATUS_DEACTIVATED' : 'BOOLEANS.DEACTIVATE_SWITCH';

    return this.translate.instant(translationKey);
  }

  async updateModulesSettings() {
    this.session.setLoading(true);
    await this.session.setFeatures(this.enabledFeatures);
    await this.updateCompanySettings();
    this.session.setLoading(false);
  }

  async updateCompanySettings() {
    this.company = await this.session.getCompanyData();

    const payload: any = {
      name: this.company.name,
      identifier: this.company.identifier,
      defaultLanguageId: this.company.defaultLanguageId,
      currency: this.company.currency,
      website: this.company.website,
      privacyPolicyLink: this.company.privacyPolicyLink,
      termsOfUseLink: this.company.termsOfUseLink,
      supportEmail: this.company.supportEmail,
      notificationSenderEmail: this.company.notificationSenderEmail,
      iosDownloadLink: this.company.iosDownloadLink,
      androidDownloadLink: this.company.androidDownloadLink,
      userManualLink: this.company.userManualLink,
      supportLink: this.company.supportLink,

      inventorySubmissionThresholdDays: this.inventorySubmissionThresholdDays,
      hasRetailerBusinessGroup: this.businessGroup,
      canCreateItemOnSale: this.allowSerialNumberCreationDuringSale,
      showIsLegacy: this.isLegacy,
      allowUserChangePassword: this.allowPasswordChange,
      showProductAttributeDetails: this.showProductAttributeDetails,
      showRetailerAttributeDetails: this.showRetailerAttributeDetails,
      showSecurityCode: this.showSecurityCode,
      showSecurityCodeVersion: this.showSecurityCodeVersion,
      repairOnlySoldItems: this.repairOnlySoldItems,
      isMfaMandatory: this.isMfaMandatory,
      digitalPassportUrl: this.digitalPassportUrl,
      isDigitalPassportSecurityPasscodeRequired: this.isDigitalPassportSecurityPasscodeRequired,
      isPrepaired: this.isPrepaired === 'true' ? true : false,
      hasItemsWithoutSerialNumber: this.hasItemsWithoutSerialNumber === '1' ? true : false,
      hasLimitedEdition: this.hasLimitedEdition === '1' ? true : false,
      hasCpo: this.hasCpo === '1' ? true : false,
      hasPrice: this.hasPrice === '1' ? true : false,
      implicitSaleCreation: this.implicitSaleCreation === '1' ? true : false,
      directImportEnabled: this.directImportEnabled === '1' ? true : false,
      warrantyUseLimit: this.warrantyUseLimit,
      allowItemOperationsSoldByAnotherRetailer: this.allowItemOperationsSoldByAnotherRetailer,
    };

    await firstValueFrom(this.companyService.apiCompanyUpdateIdPut(this.company.id ?? 0, payload));
    this.session.getCompanyData(true);
  }

  onStateChange(event: any, feature: any) {

    if (event.value) {
      this.enabledFeatures.push(feature.id);
    }
    else {
      this.enabledFeatures = this.enabledFeatures.filter(f => f != feature.id);
    }
  }

  getFeatureValue(feature: any) {
    return !!this.enabledFeatures.find(f => f == feature.id);
  }

  onImageLoadError() {
    this.imageLoadFailed = true;
  }

  async onImageRemove() {
    if (this.assistanceSerialNumberImagePath != null) {
      await firstValueFrom(this.companyService.apiCompanyAssistanceImageRemovePut());
    }
    this.assistanceSerialNumberImage = null;
    this.assistanceSerialNumberImagePath = null;

    this.assistanceImagePath = null;

    this.file = null;
    this.imageLoadFailed = false;
    this.fileUploadError = '';
  }

  showUploadPrompt() {
    this.assistanceImagePath = null;
    this.imageLoadFailed = false;
    this.file = null;
  }

  handleFileSelect(event: any) {
    this.onFileUpload(event);
    this.uploadFile();
  }

  onFileUpload(event: any) {
    const uploadedFile = event.files[0];
    if (uploadedFile && !uploadedFile.type.startsWith('image/')) {
      this.fileUploadError = this.translate.instant('SYSTEM_SETTINGS.THEME.FILE_VALIDATION');
      this.assistanceImagePath = null;
      return;
    }

    if (uploadedFile.size > 1024 * 1024) {
      this.fileUploadError = this.translate.instant('SYSTEM_SETTINGS.THEME.FILE_SIZE_VALIDATION');
      return;
    }

    this.file = uploadedFile;
    this.fileUploadError = '';
  }

  async uploadFile() {
    if (!this.file) return null;

    try {

      const toBase64 = (file: File): Promise<string> =>
        new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.readAsDataURL(file)
          reader.onload = () => resolve(reader.result as string)
          reader.onerror = (error) => reject(error)
        })


      const img = await toBase64(this.file);

      const payload: AssistanceImageRequest = {
        assistanceImage: img
      };

      const path = await firstValueFrom(this.companyService.apiCompanyAssistanceImageUploadPut(payload));

      this.assistanceImagePath = null;

      this.assistanceSerialNumberImage = this.sanitizer.bypassSecurityTrustUrl(img);
      this.imageUpdated = true;
      this.assistanceSerialNumberImagePath = path?.response ?? null;
      setTimeout(() => this.imageUpdated = false, 3000);

      this.setSerialNumberAssistanceImage = 1;


      return true;

    } catch (error) {
      console.error('File upload failed', error);
      this.toasterService.show(
        'error',
        this.translate.instant('SYSTEM_SETTINGS.THEME.ERROR_TITLE'),
        this.translate.instant('SYSTEM_SETTINGS.THEME.FILE_UPLOAD_FAILED')
      );
      return null;
    }
  }

}
